'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';

export interface BadgeProps {
  count?: number;
  /** Render even if count is 0 (default false — empty zero-badges look broken). */
  showZero?: boolean;
  /** 'accent' (default) | 'busy' (red) | 'soft' (muted). */
  tone?: 'accent' | 'busy' | 'soft';
  /** Cap displayed value (default 99). */
  cap?: number;
  className?: string;
}

const TONES: Record<NonNullable<BadgeProps['tone']>, string> = {
  accent: 'bg-accent text-text-on-accent',
  busy: 'bg-status-busy text-text-on-accent',
  soft: 'bg-surface-3 text-text-secondary',
};

/**
 * Small unread-count pill. Renders nothing when count is 0/null/undefined
 * unless showZero is true. Caps at 'cap+' (default '99+').
 */
export function Badge({
  count,
  showZero = false,
  tone = 'busy',
  cap = 99,
  className,
}: BadgeProps) {
  if (count == null) return null;
  if (count <= 0 && !showZero) return null;
  const display = count > cap ? `${cap}+` : String(count);
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold tabular-nums',
        TONES[tone],
        className
      )}
    >
      {display}
    </span>
  );
}
